import React, { useEffect, useRef, useState } from 'react';
import { Col, FormFeedback, FormGroup, Input, Label, Modal, ModalBody, ModalFooter } from 'reactstrap';
import { Cropper } from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import { FormikProps } from 'formik';
import GalleryIcon from 'assets/icons/GalleryIcon';
import DocIcon from 'assets/icons/DocIcon';
import StarIcon from 'assets/icons/StarIcon';
import WebIcon from 'assets/icons/WebIcon';
import FieldInstruction from 'Components/Common/FieldInstruction';
import AISuggestions from 'Components/onboarding/AISuggestions';
import { FIELDS_INSTRUCTIONS } from 'constants/fieldsInstructions';

export interface BrandFormValues {
    name: string;
    description: string;
    website_url: string;
    ig_handle: string;
    img_url: string | null;
}

const GeneratingFieldWrap: React.FC<{
    generating: boolean;
    isEmpty: boolean;
    alwaysAnimate?: boolean;
    children: React.ReactNode;
    className?: string;
}> = ({ generating, isEmpty, alwaysAnimate = false, children, className }) => (
    <div
        className={[className, generating && (alwaysAnimate || isEmpty) ? 'brand-field-generating' : '']
            .filter(Boolean)
            .join(' ')}
    >
        {children}
    </div>
);

export { GeneratingFieldWrap };

const Fields: React.FC<{
    formFields: FormikProps<BrandFormValues>;
    onBrandLogoChange: (logo: File) => void;
    hideName?: boolean;
    hideWebsite?: boolean;
    disabled?: boolean;
    generating?: boolean;
    igProbables?: string[];
    showIGProbables?: boolean;
}> = ({
    formFields,
    onBrandLogoChange,
    hideName = false,
    hideWebsite = false,
    disabled = false,
    generating = false,
    igProbables = [],
    showIGProbables = false,
}) => {
        const fileInputRef = useRef<HTMLInputElement | null>(null);
        const [selectedFile, setSelectedFile] = useState<File | null>(null);
        const [isDragging, setIsDragging] = useState(false);
        const [brandLogoURL, setBrandLogoURL] = useState<string | null>(null);
        const [modalOpen, setModalOpen] = useState(false);
        const [cropper, setCropper] = useState<Cropper | null>(null);
        const [croppedBrandLogo, setCroppedBrandLogo] = useState<File | null>(null)

        useEffect(() => {
            if (selectedFile) {
                setModalOpen(true); // Open the modal when a file is selected
            }
        }, [selectedFile]);

        // Set images to local states
        useEffect(() => {
            setBrandLogoURL(formFields.values.img_url ?? null);
        }, [formFields.values.img_url]);

        useEffect(() => {
            if (croppedBrandLogo)
                onBrandLogoChange(croppedBrandLogo);
        }, [croppedBrandLogo])

        const handleEditClick = () => {
            if (disabled) return;
            fileInputRef.current?.click();
        };

        const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];
            if (file) {
                setSelectedFile(file);
            }
        };

        const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
            if (disabled) return;
            event.preventDefault();
            setIsDragging(true);
        };

        const handleDragLeave = () => {
            setIsDragging(false);
        };

        const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
            if (disabled) return;
            event.preventDefault();
            setIsDragging(false);
            const file = event.dataTransfer.files?.[0];
            if (file) {
                setSelectedFile(file);
            }
        };

        const handleCrop = () => {
            if (cropper) {
                let error: string | undefined = undefined;
                // if (cropper.getCroppedCanvas().height < 512 || cropper.getCroppedCanvas().width < 512) {
                //     error = "The image dimensions must be at least 512x512 pixels."
                // }


                cropper.getCroppedCanvas().toBlob((blob) => {
                    if (blob) {
                        if (blob.size > 2 * 1024 * 1024) { // 2MB limit
                            error = "Image must not exceed 2MB.";
                        }

                        const file = new File([blob], "cropped-image.jpg", { type: "image/jpeg" });
                        setCroppedBrandLogo(file);
                        setBrandLogoURL(URL.createObjectURL(file)); // Update the preview
                        formFields.setFieldError('img_url', error);
                        closeModal(); // Close the modal
                    }
                }, "image/jpeg", 1.0);

            }
        };

        const closeModal = () => {
            setModalOpen(false);
            setSelectedFile(null); // Clear the selected file if modal is closed without cropping
        };

        const handleClearIgHandle = () => {
            formFields.setFieldValue('ig_handle', '');
        };

        const handleIgSuggestionSelect = (handle: string) => {
            formFields.setFieldValue('ig_handle', handle);
        };

        const showIgSuggestions = igProbables.length > 0 && !generating && showIGProbables;

        return (
            <React.Fragment>
                {!hideName && (
                    <>
                        <FormGroup row className='py-4 mt-3'>
                            <Col sm={4} className='my-1'>
                                <div className="d-flex gap-3">
                                    <div className="w-40px">
                                        <StarIcon className='me-2' />
                                    </div>
                                    <div className="d-flex flex-column gap-2">
                                        <Label htmlFor='name' className='form-label input-label mb-0'>
                                            Brand Name  <span className="text-danger">*</span>
                                        </Label>
                                        <FieldInstruction instruction={FIELDS_INSTRUCTIONS.BRAND.NAME} />
                                    </div>
                                </div>
                            </Col>
                            <Col sm={8} className='my-1'>
                                <Input
                                    type='text'
                                    name='name'
                                    className='form-control'
                                    onChange={formFields.handleChange}
                                    onBlur={formFields.handleBlur}
                                    value={formFields.values.name || ""}
                                    invalid={formFields.touched.name && formFields.errors.name ? true : false}
                                    placeholder='Enter Name'
                                    disabled={disabled}
                                />
                                {
                                    formFields.touched.name && formFields.errors.name &&
                                    <FormFeedback type="invalid">
                                        {formFields.errors.name}
                                    </FormFeedback>
                                }
                            </Col>
                        </FormGroup>
                        <div className="divider"></div>
                    </>
                )}
                {!hideWebsite && (
                    <>
                        <FormGroup row className='py-4 mt-3'>
                            <Col sm={4} className='my-1'>
                                <div className="d-flex gap-3">
                                    <div className="w-40px">
                                        <WebIcon className='me-2' />
                                    </div>
                                    <div className="d-flex flex-column gap-2">
                                        <Label className='input-label mb-0'>
                                            Website Link <span className="text-danger">*</span>
                                        </Label>
                                        <FieldInstruction instruction={FIELDS_INSTRUCTIONS.BRAND.WEBSITE} />
                                    </div>
                                </div>
                            </Col>
                            <Col sm={8} className='my-1'>
                                <GeneratingFieldWrap generating={generating} isEmpty={!formFields.values.website_url?.trim()}>
                                    <Input
                                        type='text'
                                        name='website_url'
                                        className='form-control'
                                        onChange={formFields.handleChange}
                                        onBlur={formFields.handleBlur}
                                        value={formFields.values.website_url || ""}
                                        invalid={formFields.touched.website_url && formFields.errors.website_url ? true : false}
                                        placeholder='Enter Website Link'
                                        disabled={disabled}
                                    />
                                </GeneratingFieldWrap>
                                {
                                    formFields.touched.website_url && formFields.errors.website_url &&
                                    <FormFeedback type="invalid">
                                        {formFields.errors.website_url}
                                    </FormFeedback>
                                }
                            </Col>
                        </FormGroup>
                        <div className="divider"></div>
                    </>
                )}
                <FormGroup row className='py-4 mt-3'>
                            <Col sm={4} className='my-1'>
                                <div className="d-flex gap-3">
                                    <div className="w-40px">
                                        <WebIcon className='me-2' />
                                    </div>
                                    <div className="d-flex flex-column gap-2">
                                        <Label className='input-label mb-0'>
                                            Instagram Handle
                                        </Label>
                                        <FieldInstruction instruction={FIELDS_INSTRUCTIONS.BRAND.INSTAGRAM_HANDLE} />
                                    </div>
                                </div>
                            </Col>
                            <Col sm={8} className='my-1'>
                                <GeneratingFieldWrap generating={generating} isEmpty={!formFields.values.ig_handle?.trim()}>
                                    <div className="position-relative brand-ig-handle-input">
                                        <Input
                                            type='text'
                                            name='ig_handle'
                                            className={`form-control ${formFields.values.ig_handle?.trim() && !disabled ? 'pe-5' : ''}`}
                                            onChange={formFields.handleChange}
                                            onBlur={formFields.handleBlur}
                                            value={formFields.values.ig_handle || ""}
                                            placeholder='Enter Instagram Handle'
                                            disabled={disabled}
                                        />
                                        {formFields.values.ig_handle?.trim() && !disabled && (
                                            <button
                                                type="button"
                                                className="brand-field-clear-btn"
                                                onClick={handleClearIgHandle}
                                                aria-label="Clear Instagram handle"
                                            >
                                                <i className="bx bx-x" />
                                            </button>
                                        )}
                                    </div>
                                </GeneratingFieldWrap>
                                {showIgSuggestions && (
                                    <div className="brand-ig-suggestions mt-3">
                                        <AISuggestions
                                            step="ig"
                                            suggestions={igProbables}
                                            activeSuggestion={formFields.values.ig_handle}
                                            onClick={handleIgSuggestionSelect}
                                        />
                                    </div>
                                )}
                            </Col>
                        </FormGroup>
                        <div className="divider"></div>
                        <FormGroup row className='py-4 mt-3'>
                            <Col sm={4} className='my-1'>
                                <div className="d-flex gap-3">
                                    <div className="w-40px">
                                        <DocIcon className='me-2' />
                                    </div>
                                    <div className="d-flex flex-column gap-2">
                                        <Label htmlFor="description" className='form-label input-label mb-0'>
                                            Description  <span className="text-danger">*</span>
                                        </Label>
                                        <FieldInstruction instruction={FIELDS_INSTRUCTIONS.BRAND.DESCRIPTION} />
                                    </div>
                                </div>
                            </Col>
                            <Col sm={8} className='my-1'>
                                <GeneratingFieldWrap generating={generating} isEmpty={!formFields.values.description?.trim()}>
                                    <Input
                                        type='textarea'
                                        name='description'
                                        className='form-control textarea-200'
                                        onChange={formFields.handleChange}
                                        onBlur={formFields.handleBlur}
                                        value={formFields.values.description || ""}
                                        invalid={formFields.touched.description && formFields.errors.description ? true : false}
                                        placeholder='Write here about brand'
                                        disabled={disabled}
                                    />
                                </GeneratingFieldWrap>
                                {
                                    formFields.touched.description && formFields.errors.description &&
                                    <FormFeedback type="invalid">
                                        {formFields.errors.description}
                                    </FormFeedback>
                                }
                            </Col>
                        </FormGroup>
                        <div className="divider"></div>
                        <FormGroup row className="py-4 mt-3">
                            <Col sm={4} className="my-1">
                                <div className="d-flex gap-3">
                                    <div className="w-40px">
                                        <span className="icon-circle me-3">
                                            <GalleryIcon />
                                        </span>
                                    </div>
                                    <div className="d-flex flex-column gap-2">
                                        <Label htmlFor="name" className="form-label input-label mb-0">
                                            Brand Logo
                                        </Label>
                                        <FieldInstruction instruction={FIELDS_INSTRUCTIONS.BRAND.LOGO} />
                                    </div>
                                </div>
                            </Col>
                            <Col sm={8} className="my-1">
                                <GeneratingFieldWrap generating={generating} isEmpty={!brandLogoURL}>
                                    <div
                                        className={`${brandLogoURL ? 'campaign-creative-img' : 'campaign-creative-img-placeholder'
                                            } ${isDragging ? 'dragging' : ''} ${disabled ? 'pe-none' : ''} ${formFields.errors.img_url && 'border-dashed-danger'}`}
                                        style={{
                                            backgroundImage: brandLogoURL ? `url(${brandLogoURL})` : undefined,
                                        }}
                                        onClick={handleEditClick}
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                    >
                                        {brandLogoURL ? (
                                            <div className="creative-action">
                                                <i className="bx bxs-pencil"></i>
                                            </div>
                                        ) : (
                                            <button type="button" className="creative-add-btn" disabled={disabled}>
                                                +
                                            </button>
                                        )}
                                    </div>
                                </GeneratingFieldWrap>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    style={{ display: 'none' }}
                                    accept=".png, .jpg, .jpeg"
                                    onChange={handleFileChange}
                                />
                                {
                                    formFields.errors.img_url &&
                                    <FormFeedback type="invalid" className='d-flex'>
                                        {formFields.errors.img_url}
                                    </FormFeedback>
                                }
                            </Col>
                        </FormGroup>
                        {/* Cropper Modal */}
                        <Modal isOpen={modalOpen} toggle={closeModal}>
                            <ModalBody>
                                {selectedFile && (
                                    <Cropper
                                        style={{ height: 400, width: '100%' }}
                                        initialAspectRatio={1}
                                        aspectRatio={1}
                                        src={URL.createObjectURL(selectedFile)}
                                        viewMode={1}
                                        guides={true}
                                        minCropBoxHeight={10}
                                        minCropBoxWidth={10}
                                        background={false}
                                        responsive={true}
                                        autoCropArea={1}
                                        checkOrientation={false}
                                        onInitialized={(instance) => setCropper(instance)}
                                    />
                                )}
                            </ModalBody>
                            <ModalFooter>
                                <button className='theme-btn theme-btn-md btn-primary-outlined' color="primary" onClick={closeModal}>
                                    Cancel
                                </button>
                                <button className='theme-btn theme-btn-md btn-gradient' color="primary" onClick={handleCrop}>
                                    Crop
                                </button>
                            </ModalFooter>
                        </Modal>
            </React.Fragment>
        );
    };

export default Fields;